import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { ToastService, Toast } from './toast.service';

// ─── Icon SVG paths por tipo ──────────────────────────────────────────────────
const ICONS = {
  success: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  error:   'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z',
  warning: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  info:    'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
} as const;

const COLORS = {
  success: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  error:   'bg-rose-50    border-rose-200    text-rose-800',
  warning: 'bg-amber-50   border-amber-200   text-amber-800',
  info:    'bg-blue-50    border-blue-200    text-blue-800',
} as const;

const ICON_COLORS = {
  success: 'text-emerald-500',
  error:   'text-rose-500',
  warning: 'text-amber-500',
  info:    'text-blue-500',
} as const;

/**
 * Toast stack — posicionado fixo no canto inferior direito.
 * Adicionar UMA vez no AppComponent (ou Shell), fora do router-outlet.
 *
 * Acessibilidade:
 * - role="log" + aria-live="polite" anuncia novos toasts sem interromper
 * - role="alert" + aria-live="assertive" para erros (interrompe)
 * - Cada toast pode ser dispensado via teclado (Enter/Space no botão ×)
 */
@Component({
  selector: 'ui-toast-stack',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!-- Região de anúncio para leitores de tela -->
    <div
      aria-live="polite"
      aria-atomic="false"
      class="sr-only"
      id="toast-announcer"
    ></div>

    <!-- Stack visual -->
    <div
      class="fixed bottom-5 right-5 z-[9000] flex flex-col-reverse gap-2 pointer-events-none"
      role="region"
      aria-label="Notificações"
    >
      @for (toast of toasts(); track toast.id) {
        <div
          class="pointer-events-auto flex items-start gap-3 w-80 max-w-[calc(100vw-2.5rem)]
                 rounded-xl border px-4 py-3 shadow-lg
                 {{ colorClass(toast) }}"
          [class.toast-enter]="!toast.leaving"
          [class.toast-leave]="toast.leaving"
          [attr.role]="toast.type === 'error' ? 'alert' : 'status'"
          [attr.aria-live]="toast.type === 'error' ? 'assertive' : 'polite'"
        >
          <!-- Ícone -->
          <svg
            class="shrink-0 w-5 h-5 mt-0.5 {{ iconColorClass(toast) }}"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"
            aria-hidden="true"
          >
            <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="iconPath(toast)" />
          </svg>

          <!-- Conteúdo -->
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold leading-snug">{{ toast.title }}</p>
            @if (toast.message) {
              <p class="text-xs mt-0.5 opacity-80 leading-snug">{{ toast.message }}</p>
            }
          </div>

          <!-- Fechar -->
          <button
            type="button"
            (click)="dismiss(toast.id)"
            class="shrink-0 opacity-60 hover:opacity-100 transition-opacity
                   -mr-1 p-0.5 rounded focus-visible:ring-2 focus-visible:ring-current"
            aria-label="Fechar notificação"
          >
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24"
              stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round"
                stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
      }
    </div>
  `,
})
export class UiToastStackComponent {
  private toastService = inject(ToastService);

  readonly toasts = this.toastService.toasts;

  dismiss(id: string): void {
    this.toastService.dismiss(id);
  }

  colorClass(t: Toast): string {
    return COLORS[t.type];
  }

  iconColorClass(t: Toast): string {
    return ICON_COLORS[t.type];
  }

  iconPath(t: Toast): string {
    return ICONS[t.type];
  }
}
